import type { AgentThinking } from '@/api/types';

/**
 * What Throughline is doing, persistently.
 *
 * The dashboard used to go quiet between checkpoints — and the only thing on
 * screen saying "Listening" was the microphone indicator, which reports that
 * audio is being captured and nothing about whether anything is thinking. A GM
 * reading that as reassurance is exactly the trap.
 *
 * Read for a fraction of a second between glances at the table, so it has to
 * resolve to a decision — keep talking, or start wrapping up — rather than to
 * a status. Hence one short line, phrased as what it is doing rather than what
 * state it is in. The fuller headline and detail live in the tooltip.
 */
interface Props {
  thinking: AgentThinking | null;
  /** True while the event stream is down; the panel must not look confident. */
  stale?: boolean;
}

const PHASE_LABEL: Record<AgentThinking['phase'], string> = {
  idle: 'Caught up',
  listening: 'Listening',
  preparing: 'Preparing the next checkpoint',
  rendering: 'Drawing scenes',
  answering: 'Answering you',
  degraded: 'Offline for a moment, still recording',
  suspended: 'Paused',
};

export function AgentStatus({ thinking, stale = false }: Props) {
  // Before the first tick there is genuinely nothing to report. Saying
  // "listening" here would be a guess, and a wrong one during startup.
  if (!thinking) return null;

  const working =
    thinking.phase === 'preparing' ||
    thinking.phase === 'rendering' ||
    thinking.phase === 'answering' ||
    thinking.phase === 'degraded';
  const label = stale ? 'Reconnecting…' : PHASE_LABEL[thinking.phase];
  const explanation = stale
    ? 'Throughline: reconnecting…'
    : thinking.detail
      ? `Throughline: ${thinking.headline} · ${thinking.detail}`
      : `Throughline: ${thinking.headline}`;

  return (
    <div
      className={`agent-status agent-status--${stale ? 'stale' : thinking.phase}`}
      data-working={working && !stale}
      aria-live="polite"
      title={explanation}
    >
      <span className="agent-status__dot" aria-hidden="true" />
      <span className="agent-status__label">
        <span className="visually-hidden">Throughline: </span>
        {label}
      </span>
    </div>
  );
}
